/**
 * Everything the book reads, and the writes it makes.
 *
 * There is no `.filter()` here standing between one company and another, and
 * none between one collector and another's accounts. `customers_read` applies
 * `can_see_customer()` in SQL, so what arrives is already what this person may
 * see, and deleting every line of presentation logic in this file would not
 * change that boundary.
 *
 * Since Session 6.6 there is no `.filter()` deciding which accounts *match*
 * either. Filtering, sorting and paging are arguments to `book_page`, so a
 * question about a book of 1,800 accounts is answered over 1,800 accounts
 * rather than over the first page of them.
 *
 * Reads are bounded. A company with forty thousand accounts is an ordinary
 * company, and a screen that loads all of them is a screen that stops working
 * exactly when the book becomes worth having.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
    ActivityEntry, ActivityKind, AnswerPromiseCall, BookFilters, BookSort, BulkAction, BulkResult, Cheque,
    ContactPerson, Customer, TenantContext,
} from '@timely/domain';
import {
    BULK_LIMIT, ChequeStatus, DEFAULT_FILTERS, FollowUpStatus, asMemberId, concentration, contactsForSaving, readAgeingWindows,
    readBulkResult, readContacts,
} from '@timely/domain';
import { supabase } from '../lib/supabaseClient';

/* ------------------------------ row shapes -------------------------------- */

interface CustomerRow {
    id: string;
    source_key: string | null;
    source_row_label: string | null;
    company: string;
    contact_person: string | null;
    contact_number: string | null;
    contact_post: string | null;
    email: string | null;
    city: string | null;
    state: string | null;
    address: string | null;
    tax_id: string | null;
    secondary_tax_id: string | null;
    credit_limit: number | string | null;
    payment_terms_days: number | null;
    payment_rank: string | null;
    category: string | null;
    total: number | string;
    total_type: string | null;
    /** The four standard bands; a TallyPrime account's also carries the company's own `windows`. */
    ageing: Record<string, unknown> | null;
    ageing_types: Record<string, string> | null;
    over90: number | string | null;
    over90_type: string | null;
    due_over45: number | string | null;
    due_over45_type: string | null;
    owner_member_id: string | null;
    collector_member_id: string | null;
    imported_owner_code: string | null;
    follow_up_date: string | null;
    forecast_amount: number | string | null;
    forecast_date: string | null;
    status: string;
    notes: string[] | null;
    is_urgent: boolean;
    is_new_customer: boolean;
    settled_at: string | null;
    last_follow_up_on: string | null;
    created_at: string;
    updated_at: string;
    promise_outcome?: string | null;
    promise_outcome_on?: string | null;
    additional_contacts?: unknown;
}

/** The columns the list needs. Deliberately not `*`: a narrower read is a narrower leak. */
const LIST_COLUMNS = `
    id, source_key, company, contact_person, contact_number, email, city, state,
    tax_id, category, payment_rank, total, total_type, ageing, ageing_types,
    over90, over90_type, due_over45, due_over45_type,
    owner_member_id, collector_member_id, imported_owner_code,
    follow_up_date, forecast_amount, forecast_date, status, is_urgent,
    settled_at, last_follow_up_on, created_at, updated_at, promise_outcome, promise_outcome_on
`;

const DETAIL_COLUMNS = `${LIST_COLUMNS}, source_row_label, contact_post, address,
    secondary_tax_id, credit_limit, payment_terms_days, notes, is_new_customer, additional_contacts`;

type Numeric = number | string | null | undefined;
const number = (value: Numeric): number => Number(value ?? 0) || 0;
const optionalNumber = (value: number | string | null | undefined): number | undefined =>
    value === null || value === undefined ? undefined : Number(value) || 0;
const date = (value: string | null): Date | undefined =>
    value ? new Date(value) : undefined;

export function toCustomer(row: CustomerRow): Customer {
    return {
        id: row.id,
        ...(row.source_key ? { sourceKey: row.source_key } : {}),
        company: row.company,
        contactPerson: row.contact_person ?? '',
        contactNumber: row.contact_number ?? '',
        ...(row.contact_post ? { contactPost: row.contact_post } : {}),
        ...(row.email ? { email: row.email } : {}),
        ...(row.city ? { city: row.city } : {}),
        ...(row.state ? { state: row.state } : {}),
        ...(row.address ? { address: row.address } : {}),
        ...(row.tax_id ? { taxId: row.tax_id } : {}),
        ...(row.secondary_tax_id ? { secondaryTaxId: row.secondary_tax_id } : {}),
        ...(row.credit_limit !== null && row.credit_limit !== undefined
            ? { creditLimit: number(row.credit_limit) } : {}),
        ...(row.payment_terms_days !== null && row.payment_terms_days !== undefined
            ? { paymentTermsDays: row.payment_terms_days } : {}),
        ...(row.payment_rank ? { paymentRank: row.payment_rank as Customer['paymentRank'] } : {}),
        ...(row.category ? { category: row.category } : {}),

        total: number(row.total),
        ...(row.total_type ? { totalType: row.total_type as Customer['totalType'] } : {}),
        ageing: {
            '1-45': number(row.ageing?.['1-45'] as Numeric),
            '46-90': number(row.ageing?.['46-90'] as Numeric),
            '91-135': number(row.ageing?.['91-135'] as Numeric),
            '>135': number(row.ageing?.['>135'] as Numeric),
            // The company's own windows (TallyPrime, docs/tally/18 §1.3): what every ageing screen shows for this account.
            ...(readAgeingWindows(row.ageing) ? { windows: readAgeingWindows(row.ageing)! } : {}),
        },
        ...(row.ageing_types ? { ageingTypes: row.ageing_types as Customer['ageingTypes'] } : {}),
        ...(row.over90 !== null ? { over90: number(row.over90) } : {}),
        ...(row.over90_type ? { over90Type: row.over90_type as Customer['over90Type'] } : {}),
        ...(row.due_over45 !== null ? { dueOver45: number(row.due_over45) } : {}),
        ...(row.due_over45_type ? { dueOver45Type: row.due_over45_type as Customer['dueOver45Type'] } : {}),

        ...(row.owner_member_id ? { ownerMemberId: asMemberId(row.owner_member_id) } : {}),
        ...(row.collector_member_id ? { collectorMemberId: asMemberId(row.collector_member_id) } : {}),
        ...(row.imported_owner_code ? { importedOwnerCode: row.imported_owner_code } : {}),

        ...(date(row.follow_up_date) ? { followUpDate: date(row.follow_up_date)! } : {}),
        ...(row.forecast_amount !== null ? { forecastAmount: optionalNumber(row.forecast_amount) } : {}),
        ...(row.forecast_date ? { forecastDate: row.forecast_date } : {}),
        status: (row.status as FollowUpStatus) ?? FollowUpStatus.Pending,
        notes: row.notes ?? [],
        isUrgent: row.is_urgent,
        ...(row.is_new_customer ? { isNewCustomer: true } : {}),
        ...(row.settled_at ? { settledAt: row.settled_at } : {}),
        ...(date(row.last_follow_up_on) ? { lastFollowUpOn: date(row.last_follow_up_on)! } : {}),
        createdAt: new Date(row.created_at),
        ...(row.promise_outcome === 'kept' || row.promise_outcome === 'not_kept' ? { promiseOutcome: row.promise_outcome } : {}),
        ...(row.promise_outcome_on ? { promiseOutcomeOn: row.promise_outcome_on } : {}),
        ...(row.additional_contacts !== undefined ? { additionalContacts: readContacts(row.additional_contacts) } : {}),
    };
}

/** `updated_at` is not on `Customer`; the concurrency check needs it separately. */
export const updatedAtOf = (row: { updated_at: string }): string => row.updated_at;

/* ------------------------------- the book --------------------------------- */

/**
 * How many accounts one page of the book holds.
 *
 * Small, and it can be, because the filtering no longer happens here: a page
 * is what somebody reads, not the population a question is asked of. The
 * database clamps anything larger than 200, so this is also the most that can
 * usefully be asked for.
 */
const BOOK_PAGE = 100;

export interface TeamMemberName {
    memberId: string;
    displayName: string;
    code: string | null;
}

/**
 * The aggregate over the whole filtered book — not over the page.
 *
 * `matchedCount` and `outstanding` are of every account that matches the
 * filters and that this person may see. `visibleCount` is the size of their
 * whole book, so a screen can say "50 of 604" and mean it.
 */
export interface ServerTotals {
    matchedCount: number;
    outstanding: number;
    visibleCount: number;
}

/** Everything that decides which accounts the database returns. */
export interface BookQuery {
    filters: BookFilters;
    sort: BookSort;
}

export interface BookData {
    customers: Customer[];
    members: TeamMemberName[];
    /** Of the whole filtered book, not of what has been loaded. */
    totals: ServerTotals | null;
    /** True when the filtered book has more accounts than have been loaded. */
    hasMore: boolean;
    loadedCount: number;
}

const EMPTY: BookData = {
    customers: [], members: [], totals: null, hasMore: false, loadedCount: 0,
};

/** The arguments both RPCs take, built once so they cannot disagree. */
export const filterArgs = (workspaceId: string, filters: BookFilters) => ({
    p_workspace: workspaceId,
    p_search: filters.search,
    p_settlement: filters.settlement,
    p_follow_up: filters.followUp,
    p_ageing: filters.ageing,
    p_owner: filters.owner,
    p_rank: filters.rank,
    p_urgent_only: filters.urgentOnly,
    // Sent only when narrowing, so a call is exactly the question the screen shows.
    p_category: filters.category.length > 0 ? filters.category : null,
    p_contact: filters.contact,
});

/** Every value a book question depends on, for a query key (see `useBook`). */
export const filterKey = (filters: BookFilters): unknown[] => [
    filters.search, filters.settlement, filters.followUp, filters.ageing,
    filters.owner, filters.rank, filters.urgentOnly, filters.category, filters.contact,
];

/**
 * The book, filtered and counted where the book actually is.
 *
 * Until Session 6.6 this loaded 500 accounts and filtered them in the browser,
 * which meant a filter answered a question about the first 500 accounts and
 * the screen had to say so. A collector filtering to "overdue, over 90 days,
 * mine" on a book of 1,800 is asking about 1,800, and an answer drawn from 500
 * is the wrong answer however carefully it is labelled.
 *
 * So the filters, the sort and the paging are all arguments to `book_page`,
 * and the aggregates come from `book_totals` over the same predicate. Both are
 * `security invoker`, so `customers_read` — and therefore
 * `can_see_customer()` — decides what either can reach. The browser derives
 * labels for the rows it was given and decides nothing about which unseen
 * customers qualify.
 */
export function useBook(context: TenantContext, query: BookQuery) {
    const workspaceId = context.workspace.id;
    const [data, setData] = useState<BookData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { filters, sort } = query;

    // Every value the query depends on, in one string. A change to any of them
    // is a different question and starts again from the first page.
    // JSON rather than a joined string: a search box can contain any character,
    // including whatever separator seemed safe.
    const key = JSON.stringify([workspaceId, ...filterKey(filters), sort]);

    // The question a read was issued for. An answer that arrives after the
    // filters moved on describes a question nobody is asking any more — and,
    // when the company itself changed, one nobody may ask.
    const issuedFor = useRef(key);

    const load = useCallback(async (from = 0) => {
        issuedFor.current = key;
        if (from === 0) setLoading(true); else setLoadingMore(true);
        setError(null);

        const client = supabase();
        const args = filterArgs(workspaceId, filters);

        const [pageResult, totalsResult, membersResult] = await Promise.all([
            client.rpc('book_page', { ...args, p_sort: sort, p_limit: BOOK_PAGE, p_offset: from }),
            // Only with the first page: the totals cannot change between one
            // page of a question and the next, and asking again would be a
            // second full scan per press of "load more".
            from === 0
                ? client.rpc('book_totals', args)
                : Promise.resolve({ data: null, error: null }),
            from === 0
                ? client.from('workspace_members')
                    .select('id, display_name, code')
                    .eq('workspace_id', workspaceId).eq('status', 'active')
                : Promise.resolve({ data: null, error: null }),
        ]);

        if (issuedFor.current !== key) return;

        if (pageResult.error) {
            setError('We could not load your book. Try again in a moment.');
            setLoading(false);
            setLoadingMore(false);
            return;
        }

        const page = ((pageResult.data ?? []) as unknown as CustomerRow[]).map(toCustomer);
        const members = membersResult.data
            ? ((membersResult.data ?? []) as { id: string; display_name: string; code: string | null }[])
                .map(m => ({ memberId: m.id, displayName: m.display_name, code: m.code }))
            : undefined;

        const row = (Array.isArray(totalsResult.data) ? totalsResult.data[0] : totalsResult.data) as
            { matched_count: number; outstanding: number; visible_count: number } | null | undefined;
        const totals: ServerTotals | null = totalsResult.error
            ? null
            : row
                ? {
                    matchedCount: Number(row.matched_count) || 0,
                    outstanding: Number(row.outstanding) || 0,
                    visibleCount: Number(row.visible_count) || 0,
                }
                : null;

        setData(previous => {
            const loadedCount = (from === 0 ? 0 : previous.loadedCount) + page.length;
            const matched = (from === 0 ? totals : previous.totals)?.matchedCount;
            return {
                customers: from === 0 ? page : [...previous.customers, ...page],
                members: members ?? previous.members,
                totals: from === 0 ? totals : previous.totals,
                // The aggregate knows how many there are, so "more to load" is
                // a fact rather than the guess a full page used to be.
                hasMore: matched === undefined
                    ? page.length === BOOK_PAGE
                    : loadedCount < matched,
                loadedCount,
            };
        });
        setLoading(false);
        setLoadingMore(false);
        // `filters` and `sort` are covered by `key`, which is what a change to
        // either produces; depending on the objects themselves would reload on
        // every render, because `filtersFromParams()` builds a new one.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    useEffect(() => { void load(0); }, [load]);

    return {
        ...data,
        loading,
        loadingMore,
        error,
        reload: () => load(0),
        loadMore: () => load(data.loadedCount),
    };
}

/** How many of the largest accounts the concentration line names. */
export const CONCENTRATION_TOP = 5;

/**
 * "The 5 largest accounts hold 44% of what is owed" (Customer 360, 02 §7): the largest debit balances over all
 * that is owed, among the accounts this person can see. Both figures come from the book's own calls over the accounts
 * that owe (`book_page`, `book_totals`, security invoker), so it is never a figure about accounts they may not see.
 * Nothing when the book has no more owing accounts than the line would name: "the 3 largest hold 100%" says nothing.
 */
export function useConcentration(workspaceId: string, enabled: boolean): ReturnType<typeof concentration> {
    const [value, setValue] = useState<ReturnType<typeof concentration>>(null);
    useEffect(() => {
        if (!enabled) { setValue(null); return; }
        let current = true;
        const args = filterArgs(workspaceId, { ...DEFAULT_FILTERS, settlement: 'owes' });
        void Promise.all([
            supabase().rpc('book_page', { ...args, p_sort: 'amount-desc', p_limit: CONCENTRATION_TOP, p_offset: 0 }),
            supabase().rpc('book_totals', args),
        ]).then(([page, totals]) => {
            if (!current) return;
            const row = (Array.isArray(totals.data) ? totals.data[0] : totals.data) as { matched_count: number; outstanding: number } | null | undefined;
            if (page.error || totals.error || !row || (Number(row.matched_count) || 0) <= CONCENTRATION_TOP) { setValue(null); return; }
            const largest = ((page.data ?? []) as unknown as CustomerRow[]).map(toCustomer)
                .filter(c => c.totalType !== 'Cr').map(c => ({ amount: Math.abs(Number(c.total) || 0) }));
            setValue(largest.length === CONCENTRATION_TOP ? concentration(largest, Number(row.outstanding) || 0) : null);
        });
        return () => { current = false; };
    }, [workspaceId, enabled]);
    return value;
}

/**
 * The largest debit balances among the accounts this person can see — narrowed to one person's accounts when a report
 * is (`owner`, as Customers reads it) — for "Where it concentrates" (Customer 360, 02 §8). From `book_page` over the
 * accounts that owe, largest first; null until read, or when it cannot be.
 */
export function useLargestAccounts(workspaceId: string, owner: string, limit = CONCENTRATION_TOP): { id: string; company: string; amount: number }[] | null {
    const [rows, setRows] = useState<{ id: string; company: string; amount: number }[] | null>(null);
    useEffect(() => {
        let current = true;
        const args = filterArgs(workspaceId, { ...DEFAULT_FILTERS, settlement: 'owes', owner });
        void supabase().rpc('book_page', { ...args, p_sort: 'amount-desc', p_limit: limit, p_offset: 0 })
            .then(({ data, error }) => {
                if (!current) return;
                setRows(error ? null : ((data ?? []) as unknown as CustomerRow[]).map(toCustomer)
                    .filter(c => c.totalType !== 'Cr')
                    .map(c => ({ id: c.id, company: c.company, amount: Math.abs(Number(c.total) || 0) })));
            });
        return () => { current = false; };
    }, [workspaceId, owner, limit]);
    return rows;
}

/* --------------------------- working through a list ----------------------- */

/** The account before and after this one, in the list somebody arrived from. */
export interface Neighbours {
    previous: { id: string; company: string } | null;
    next: { id: string; company: string } | null;
    /** Which of the list this account is, one-based, for "4 of 37". */
    position: number;
    /**
     * How many accounts the list holds — all of them, as the list itself counts them (`book_totals`), not only
     * the window walked here; the window's own length if the count could not be read.
     */
    total: number;
}

/**
 * How many accounts a previous/next walk looks across.
 *
 * Bounded deliberately. A collector works down a list; they do not page through
 * two thousand accounts one at a time, and loading two thousand to find one
 * neighbour would make every account slow to open for a feature used at the
 * edges.
 */
const NEIGHBOUR_WINDOW = 200;

/**
 * Previous and next, from the list this person actually has.
 *
 * The sequence comes from `book_page` with the filters the screen was opened
 * with — the same authorised, filtered question the book asked — so it cannot
 * walk into an account outside their scope, and it cannot walk into another
 * company's book at all. There is no "all customers" ordering anywhere for it
 * to fall back to.
 *
 * When the account is not in the window (somebody opened a link directly, or it
 * is far down a long list) there is simply no previous and no next, which is
 * honest and costs nothing.
 */
export function useNeighbours(
    workspaceId: string,
    customerId: string,
    query: BookQuery,
): Neighbours | null {
    const [neighbours, setNeighbours] = useState<Neighbours | null>(null);
    const { filters, sort } = query;

    const key = JSON.stringify([workspaceId, customerId, ...filterKey(filters), sort]);
    const issuedFor = useRef(key);

    useEffect(() => {
        issuedFor.current = key;
        let abandoned = false;

        void (async () => {
            const args = filterArgs(workspaceId, filters);
            // The list's own count beside the window, so "4 of 619" says what Customers says, not "4 of 200".
            const [{ data, error }, totals] = await Promise.all([
                supabase().rpc('book_page', { ...args, p_sort: sort, p_limit: NEIGHBOUR_WINDOW, p_offset: 0 }),
                supabase().rpc('book_totals', args),
            ]);
            if (abandoned || issuedFor.current !== key || error) return;

            const list = ((data ?? []) as { id: string; company: string }[]);
            const at = list.findIndex(row => row.id === customerId);
            if (at < 0) { setNeighbours(null); return; }
            const counted = totals.error ? null : (Array.isArray(totals.data) ? totals.data[0] : totals.data) as { matched_count?: number } | null;
            const matched = Number(counted?.matched_count);

            setNeighbours({
                previous: at > 0 ? { id: list[at - 1]!.id, company: list[at - 1]!.company } : null,
                next: at < list.length - 1 ? { id: list[at + 1]!.id, company: list[at + 1]!.company } : null,
                position: at + 1,
                total: Number.isFinite(matched) && matched >= list.length ? matched : list.length,
            });
        })();

        return () => { abandoned = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return neighbours;
}

/* ------------------------------ one account ------------------------------- */

export interface CustomerDetail {
    customer: Customer;
    /** For the optimistic-concurrency check on a follow-up. */
    updatedAt: string;
    activity: ActivityEntry[];
    cheques: Cheque[];
    hasMoreActivity: boolean;
}

const ACTIVITY_PAGE = 30;

interface ActivityRow {
    id: string;
    customer_id: string;
    author_member_id: string | null;
    author_name: string;
    kind: string;
    body: string;
    promised_amount: number | string | null;
    promised_on: string | null;
    resolves_id: string | null;
    created_at: string;
}

const toActivity = (row: ActivityRow): ActivityEntry => ({
    id: row.id,
    customerId: row.customer_id,
    ...(row.author_member_id ? { authorMemberId: asMemberId(row.author_member_id) } : {}),
    authorName: row.author_name,
    kind: row.kind as ActivityKind,
    body: row.body,
    ...(row.promised_amount !== null ? { promisedAmount: number(row.promised_amount) } : {}),
    ...(row.promised_on ? { promisedOn: row.promised_on } : {}),
    ...(row.resolves_id ? { resolvesId: row.resolves_id } : {}),
    createdAt: row.created_at,
});

interface ChequeRow {
    id: string;
    customer_id: string | null;
    customer_name: string;
    cheque_number: string;
    bank_name: string;
    cheque_date: string | null;
    amount: number | string;
    status: string;
    received_date: string | null;
    cleared_date: string | null;
    remarks: string | null;
}

const toCheque = (row: ChequeRow): Cheque => ({
    id: row.id,
    customerId: row.customer_id ?? '',
    customerName: row.customer_name,
    chequeNumber: row.cheque_number,
    bankName: row.bank_name,
    // A cheque with no date is not a cheque anybody can bank, but the row can
    // exist half-entered; the epoch keeps `chequeState()` total rather than
    // making every caller guard for null.
    chequeDate: row.cheque_date ? new Date(row.cheque_date) : new Date(0),
    amount: number(row.amount),
    status: row.status as ChequeStatus,
    receivedDate: row.received_date ? new Date(row.received_date) : new Date(0),
    ...(row.cleared_date ? { clearedDate: new Date(row.cleared_date) } : {}),
    ...(row.remarks ? { remarks: row.remarks } : {}),
});

/**
 * One account, or the honest answer that it is not available.
 *
 * "Not available" covers three cases deliberately — it is in another company,
 * it is outside this person's scope, or it does not exist. The database gives
 * one answer for all three, and so does this: a detail page that distinguished
 * them would confirm which ids are real.
 */
export type DetailResult =
    | { status: 'ok'; detail: CustomerDetail }
    | { status: 'not-available' }
    | { status: 'error'; message: string };

export function useCustomerDetail(context: TenantContext, customerId: string) {
    const workspaceId = context.workspace.id;
    const [result, setResult] = useState<DetailResult | null>(null);
    const [loading, setLoading] = useState(true);
    const issuedFor = useRef(`${workspaceId}:${customerId}`);

    const load = useCallback(async () => {
        const key = `${workspaceId}:${customerId}`;
        issuedFor.current = key;
        setLoading(true);

        const client = supabase();
        const [customerResult, activityResult, chequesResult] = await Promise.all([
            client.from('customers').select(DETAIL_COLUMNS)
                .eq('workspace_id', workspaceId).eq('id', customerId).maybeSingle(),
            client.from('customer_activity')
                .select('id, customer_id, author_member_id, author_name, kind, body, promised_amount, promised_on, resolves_id, created_at')
                .eq('workspace_id', workspaceId).eq('customer_id', customerId)
                .order('created_at', { ascending: false }).order('id', { ascending: false })
                .range(0, ACTIVITY_PAGE - 1),
            client.from('pdc_cheques')
                .select('id, customer_id, customer_name, cheque_number, bank_name, cheque_date, amount, status, received_date, cleared_date, remarks')
                .eq('workspace_id', workspaceId).eq('customer_id', customerId)
                .order('cheque_date', { ascending: true }),
        ]);

        if (issuedFor.current !== key) return;

        if (customerResult.error) {
            setResult({ status: 'error', message: 'We could not load that account. Try again in a moment.' });
            setLoading(false);
            return;
        }
        if (!customerResult.data) {
            setResult({ status: 'not-available' });
            setLoading(false);
            return;
        }

        const row = customerResult.data as unknown as CustomerRow;
        const activityRows = (activityResult.data ?? []) as unknown as ActivityRow[];

        setResult({
            status: 'ok',
            detail: {
                customer: toCustomer(row),
                updatedAt: updatedAtOf(row),
                activity: activityRows.map(toActivity),
                // A refusal here is not an error: somebody without `cheques.view`
                // simply sees no cheques.
                cheques: ((chequesResult.data ?? []) as unknown as ChequeRow[]).map(toCheque),
                hasMoreActivity: activityRows.length === ACTIVITY_PAGE,
            },
        });
        setLoading(false);
    }, [workspaceId, customerId]);

    useEffect(() => { void load(); }, [load]);

    return { result, loading, reload: load };
}

/** The next page of a thread, oldest-ward. */
export async function loadMoreActivity(
    workspaceId: string, customerId: string, alreadyLoaded: number,
): Promise<{ entries: ActivityEntry[]; hasMore: boolean }> {
    const { data, error } = await supabase()
        .from('customer_activity')
        .select('id, customer_id, author_member_id, author_name, kind, body, promised_amount, promised_on, resolves_id, created_at')
        .eq('workspace_id', workspaceId).eq('customer_id', customerId)
        .order('created_at', { ascending: false }).order('id', { ascending: false })
        .range(alreadyLoaded, alreadyLoaded + ACTIVITY_PAGE - 1);

    if (error) throw new Error('We could not load any more of this history.');
    const rows = (data ?? []) as unknown as ActivityRow[];
    return { entries: rows.map(toActivity), hasMore: rows.length === ACTIVITY_PAGE };
}

/* -------------------------------- writing --------------------------------- */

export interface FollowUpResult {
    activityId: string;
    updatedAt: string;
    customerId: string;
}

/**
 * The reason a follow-up was refused, in words a person can act on.
 *
 * The database's own sentences are written for this. What is not passed through
 * is anything unrecognised — an unfamiliar error string is the one most likely
 * to carry a constraint name or an identifier.
 */
function followUpFailure(message: string | undefined): string {
    const text = (message ?? '').toLowerCase();
    if (text.includes('changed while you were working')) {
        return 'Somebody else updated this account while you were writing. Reload to see what they did, then try again.';
    }
    if (text.includes('not available')) {
        return 'That account is no longer available to you.';
    }
    if (text.includes('cannot record follow-ups')) {
        return 'You do not have permission to record follow-ups here.';
    }
    if (text.includes('promised amount')) return 'A promised amount cannot be negative.';
    if (text.includes('unknown follow-up outcome')) return 'That is not an outcome this product records.';
    return 'That did not save. Reload and try again.';
}

export async function recordFollowUp(
    call: Record<string, unknown>,
): Promise<FollowUpResult> {
    const { data, error } = await supabase().rpc('record_follow_up', call);
    if (error) throw new Error(followUpFailure(error.message));
    return data as unknown as FollowUpResult;
}

/**
 * Marking an account urgent, which is a judgement rather than a figure.
 *
 * Goes through the ordinary update policy — `customers.edit` plus
 * `can_see_customer()` — so a person who may not edit the account changes
 * nothing, whatever this function does.
 */
export async function setUrgent(customerId: string, workspaceId: string, urgent: boolean): Promise<void> {
    const { error } = await supabase()
        .from('customers').update({ is_urgent: urgent })
        .eq('id', customerId).eq('workspace_id', workspaceId);
    if (error) {
        throw new Error(/row-level security|permission denied/i.test(error.message)
            ? 'You do not have permission to change this account.'
            : 'That did not save. Try again in a moment.');
    }
}


/** Everybody's name in this company, by member id, for saying whose an account is. */
export function useMemberNames(workspaceId: string): Map<string, string> {
    const [names, setNames] = useState<Map<string, string>>(() => new Map());
    useEffect(() => {
        let current = true;
        void supabase().from('workspace_members').select('id, display_name, code').eq('workspace_id', workspaceId)
            .then(({ data }) => {
                if (!current || !data) return;
                const rows = data as { id: string; display_name: string | null; code: string | null }[];
                setNames(new Map(rows.map(m => [m.id, m.display_name || m.code || 'Somebody'])));
            });
        return () => { current = false; };
    }, [workspaceId]);
    return names;
}

/* ----------------------- what was last said, per account ------------------ */

export interface LastNote {
    kind: ActivityKind;
    body: string;
    authorName: string;
    createdAt: string;
}

/**
 * The newest entry a person wrote on each account of a page (`last_notes`,
 * 0033), for the line under a name. Invoker: an account outside the caller's
 * scope returns nothing. One call per page of ids, never one per row.
 */
export function useLastNotes(workspaceId: string, customerIds: readonly string[]): Map<string, LastNote> {
    const [notes, setNotes] = useState<Map<string, LastNote>>(() => new Map());
    const key = JSON.stringify([workspaceId, customerIds]);
    useEffect(() => {
        if (customerIds.length === 0) { setNotes(new Map()); return; }
        let current = true;
        const chunks: string[][] = [];
        for (let i = 0; i < customerIds.length; i += 200) chunks.push(customerIds.slice(i, i + 200));
        void Promise.all(chunks.map(ids => supabase().rpc('last_notes', { p_workspace: workspaceId, p_customers: ids })))
            .then(results => {
                if (!current) return;
                const map = new Map<string, LastNote>();
                for (const { data, error } of results) {
                    if (error) continue;
                    const list = Array.isArray(data) ? data as { customer_id: string; kind: string; body: string; author_name: string; created_at: string }[] : [];
                    for (const row of list) {
                        map.set(row.customer_id, { kind: row.kind as ActivityKind, body: row.body, authorName: row.author_name, createdAt: row.created_at });
                    }
                }
                setNotes(map);
            });
        return () => { current = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return notes;
}

/* ------------------------- writing to one account ------------------------- */

/** A refusal from the database, in words a person can act on. Unknown strings are not passed through. */
export function accountWriteFailure(message: string | undefined): string {
    const text = (message ?? '').toLowerCase();
    if (text.includes('changed while you were working')) {
        return 'Somebody else updated this account while you were working. Reload to see what changed, then try again.';
    }
    if (text.includes('not available')) return 'That account is no longer available to you.';
    if (text.includes('already been answered')) return 'Somebody has already answered that promise. Reload to see how.';
    if (text.includes('not on this account')) return 'That promise is not on this account any more. Reload and try again.';
    if (text.includes('contact details are not valid') || text.includes('customers_contacts_shape')) {
        return 'Those contact details could not be saved. Each person needs a phone number or an email.';
    }
    if (text.includes('cannot change many')) return 'You do not have permission to change many accounts at once.';
    if (text.includes('cannot reassign')) return 'You do not have permission to reassign accounts.';
    if (text.includes('cannot record follow-ups')) return 'You do not have permission to record follow-ups here.';
    if (text.includes('cannot change accounts') || /row-level security|permission denied/.test(text)) {
        return 'You do not have permission to change this account.';
    }
    if (text.includes('not an active member')) return 'That person is no longer an active member of this company.';
    if (text.includes('choose today or later')) return 'Choose today or a later day.';
    if (text.includes('within the next year')) return 'Choose a day within the next year.';
    if (text.includes('at most 500')) return `At most ${BULK_LIMIT} accounts can be changed at once.`;
    return 'That did not save. Reload and try again.';
}

/** An account's rank, or back to automatic (`null`) — `set_payment_rank`, 0033. */
export async function setPaymentRank(
    workspaceId: string, customerId: string, rank: 'Good' | 'Late' | 'Bad' | null, expectedUpdatedAt: string | null,
): Promise<void> {
    const { error } = await supabase().rpc('set_payment_rank', {
        p_workspace: workspaceId, p_customer: customerId, p_rank: rank, p_expected_updated_at: expectedUpdatedAt,
    });
    if (error) throw new Error(accountWriteFailure(error.message));
}

/** The other people to reach on an account, replacing the list — `set_customer_contacts`, 0033. */
export async function saveContacts(
    workspaceId: string, customerId: string, contacts: readonly ContactPerson[], expectedUpdatedAt: string | null,
): Promise<void> {
    const { error } = await supabase().rpc('set_customer_contacts', {
        p_workspace: workspaceId, p_customer: customerId,
        p_contacts: contactsForSaving(contacts), p_expected_updated_at: expectedUpdatedAt,
    });
    if (error) throw new Error(accountWriteFailure(error.message));
}

/** "Kept" or "Not kept", on the promise itself — `answer_promise`, 0033. */
export async function answerPromise(call: AnswerPromiseCall): Promise<void> {
    const { error } = await supabase().rpc('answer_promise', call as unknown as Record<string, unknown>);
    if (error) throw new Error(accountWriteFailure(error.message));
}

/* ------------------------- changing many accounts ------------------------- */

/**
 * One change to a chosen set of accounts — `customers_bulk_update`, 0033. A
 * dry run runs every check and writes nothing; the confirmation shows its
 * counts, then the same call is made for real.
 */
export async function bulkUpdateCustomers(
    workspaceId: string, customerIds: readonly string[], action: BulkAction, value: string | null, dryRun: boolean,
): Promise<BulkResult> {
    const { data, error } = await supabase().rpc('customers_bulk_update', {
        p_workspace: workspaceId, p_customers: customerIds, p_action: action, p_value: value, p_dry_run: dryRun,
    });
    if (error) throw new Error(accountWriteFailure(error.message));
    return readBulkResult(data);
}

/**
 * The ids of every account the filters match, up to the bulk limit, in the
 * list's own order — for "choose all". Asked of `book_page` under the caller's
 * own authorisation, so it holds nothing they could not already see.
 */
export async function matchingIds(workspaceId: string, filters: BookFilters, sort: BookSort, limit = BULK_LIMIT): Promise<string[]> {
    const ids: string[] = [];
    for (let offset = 0; offset < limit; offset += 200) {
        const { data, error } = await supabase().rpc('book_page', {
            ...filterArgs(workspaceId, filters), p_sort: sort, p_limit: Math.min(200, limit - offset), p_offset: offset,
        });
        if (error) throw new Error('We could not choose those accounts. Try again in a moment.');
        const page = (data ?? []) as { id: string }[];
        ids.push(...page.map(r => r.id));
        if (page.length < 200) break;
    }
    return ids.slice(0, limit);
}

/**
 * The categories on the accounts this person may see, with how many owe
 * something in each (`book_categories`, 0033) — for the category filter.
 * `''` is the accounts with none set.
 */
export function useCategories(workspaceId: string): { category: string; count: number }[] {
    const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
    useEffect(() => {
        let current = true;
        void supabase().rpc('book_categories', { p_workspace: workspaceId }).then(({ data, error }) => {
            if (!current || error) return;
            setCategories((Array.isArray(data) ? data as { category: string; account_count: number }[] : [])
                .map(row => ({ category: row.category ?? '', count: Number(row.account_count) || 0 })));
        });
        return () => { current = false; };
    }, [workspaceId]);
    return categories;
}
